import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { useTheme } from "@/context/ThemeContext";
import { usePermissions } from "@/hooks/usePermissions";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuLabel, DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent, DropdownMenuPortal,
} from "@/components/ui/dropdown-menu";
import {
  User, LogOut, LayoutDashboard, Briefcase, Ticket, MapPin, Phone, Heart, Package, Settings, Moon, Sun, MessageSquare, Megaphone, HelpCircle, LayoutTemplate, ShieldCheck, CreditCard,
  ChevronDown, Users,
} from "lucide-react";
import * as LucideIcons from "lucide-react";
import { api } from "@/lib/api";

const getNavIcon = (name) => {
  if (!name) return LucideIcons.MapPin;
  // Direct match (PascalCase)
  if (LucideIcons[name]) return LucideIcons[name];
  // Try PascalCase conversion: "bus" → "Bus", "footprints" → "Footprints"
  const pascal = name.charAt(0).toUpperCase() + name.slice(1);
  if (LucideIcons[pascal]) return LucideIcons[pascal];
  // Try full PascalCase for multi-word: "heart-pulse" → "HeartPulse"
  const camel = name.replace(/(^|[-_ ])(\w)/g, (_, __, c) => c.toUpperCase());
  return LucideIcons[camel] || LucideIcons.MapPin;
};

export default function Header() {
  const { user, logout } = useAuth();
  const { settings } = useSettings();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [navCategories, setNavCategories] = useState([]);
  const [navProducts, setNavProducts] = useState({});
  const [openDropdown, setOpenDropdown] = useState(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const dropdownTimeout = useRef(null);

  useEffect(() => {
    api.get("/categories").then((r) => {
      const cats = r.data.filter((c) => !c.parent_id);
      setNavCategories(cats);
      // Preload top 5 products per category for dropdowns
      cats.forEach((cat) => {
        const params = new URLSearchParams(cat.link_query || `category=${cat.product_category}`);
        api.get("/products", { params: { ...Object.fromEntries(params), limit: 5 } }).then((res) => {
          if (res.data && res.data.length > 0) {
            setNavProducts((prev) => ({ ...prev, [cat.slug]: res.data }));
          }
        }).catch(() => {});
      });
    }).catch(() => {});
  }, []);

  const showDropdown = useCallback((slug, e) => {
    clearTimeout(dropdownTimeout.current);
    const rect = e.currentTarget.getBoundingClientRect();
    setDropdownPos({ top: rect.bottom, left: rect.left });
    setOpenDropdown(slug);
  }, []);

  const hideDropdown = useCallback(() => {
    dropdownTimeout.current = setTimeout(() => setOpenDropdown(null), 150);
  }, []);

  const keepDropdown = useCallback(() => {
    clearTimeout(dropdownTimeout.current);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const { permissions, isStaff } = usePermissions();

  return (
    <header className="sticky top-0 z-40 glass border-b border-border" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Row 1: Logo + Controls */}
        <div className="flex items-center justify-between h-14 lg:h-16">
          <Link to="/" className="flex items-center gap-2.5" data-testid="nav-logo">
            {settings.logo_url ? (
              <img src={settings.logo_url} alt={settings.site_name || "HyderabadTourism.in"} className="h-10 w-auto object-contain rounded-xl" />
            ) : (
              <div className="relative w-10 h-10 rounded-xl overflow-hidden shadow-md" style={{background: "linear-gradient(135deg, #00B7EB 0%, #008FA4 55%, #006B85 100%)"}}>
                <div className="absolute inset-0 flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-white drop-shadow" strokeWidth={2.4} />
                </div>
              </div>
            )}
            <div className="leading-tight">
              <div className="font-display text-lg lg:text-xl font-bold tracking-tight" style={{color: "var(--brand-primary, #054F63)"}}>
                {settings.site_name || "HyderabadTourism.in"}
              </div>
              <div className="text-[9px] lg:text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-medium">Est. {settings.trust_badges?.est_year || "2005"} · Trusted since</div>
            </div>
          </Link>

          <div className="flex items-center gap-2 sm:gap-3">
            <Link to="/enquire" className="hidden md:flex items-center gap-1.5 text-sm text-saffron font-semibold hover:text-saffron-dark transition-colors" data-testid="nav-enquire">
              <MessageSquare className="w-4 h-4" />
              Enquire
            </Link>
            <a href={`tel:${(settings.contact_phone || "").replace(/\s/g, "")}`} className="hidden md:flex items-center gap-2 text-sm text-teal font-medium" data-testid="nav-phone">
              <Phone className="w-4 h-4" />
              {settings.contact_phone || "+91 90000 00000"}
            </a>

            {user && (
              <Link to="/wishlist" className="hidden sm:flex w-9 h-9 rounded-lg items-center justify-center hover:bg-secondary transition-colors" data-testid="nav-wishlist">
                <Heart className="w-4 h-4 text-saffron" />
              </Link>
            )}

            <button
              onClick={toggle}
              className="w-9 h-9 rounded-lg flex items-center justify-center hover:bg-secondary transition-colors"
              aria-label="Toggle dark mode"
              title={theme === "dark" ? "Switch to light" : "Switch to dark"}
              data-testid="theme-toggle"
            >
              {theme === "dark" ? <Sun className="w-4 h-4 text-teal" /> : <Moon className="w-4 h-4 text-teal" />}
            </button>

            {!user ? (
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={() => navigate("/login")} data-testid="nav-login-btn">Sign In</Button>
                <Button className="bg-saffron hover:bg-saffron-dark text-white" size="sm" onClick={() => navigate("/register")} data-testid="nav-register-btn">Register</Button>
              </div>
            ) : (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="gap-2" data-testid="nav-user-menu">
                    <User className="w-4 h-4" />
                    {user.name?.split(" ")[0] || "Account"}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="font-medium">{user.name}</div>
                    <div className="text-xs text-muted-foreground">{user.email}</div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/my-bookings")} data-testid="menu-my-bookings">
                    <Ticket className="w-4 h-4 mr-2" /> My Bookings
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-profile">
                    <User className="w-4 h-4 mr-2" /> My Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/wishlist")} data-testid="menu-wishlist">
                    <Heart className="w-4 h-4 mr-2" /> Wishlist
                  </DropdownMenuItem>
                  {isStaff && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuSub>
                        <DropdownMenuSubTrigger>
                          <LayoutDashboard className="w-4 h-4 mr-2" /> Admin
                        </DropdownMenuSubTrigger>
                        <DropdownMenuPortal>
                          <DropdownMenuSubContent>
                            {permissions.dashboard && (
                              <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin-dashboard">
                                <LayoutDashboard className="w-4 h-4 mr-2" /> Overview
                              </DropdownMenuItem>
                            )}
                            {permissions.products_view && (
                              <DropdownMenuItem onClick={() => navigate("/admin/products")} data-testid="menu-admin-products">
                                <Package className="w-4 h-4 mr-2" /> Products
                              </DropdownMenuItem>
                            )}
                            {permissions.bookings_view && (
                              <DropdownMenuItem onClick={() => navigate("/admin/bookings")} data-testid="menu-admin-bookings">
                                <CreditCard className="w-4 h-4 mr-2" /> Bookings
                              </DropdownMenuItem>
                            )}
                            {permissions.blog_view && (
                              <DropdownMenuItem onClick={() => navigate("/admin/blog")} data-testid="menu-admin-blog">
                                <Package className="w-4 h-4 mr-2" /> Blog
                              </DropdownMenuItem>
                            )}
                            {permissions.enquiries_view && (
                              <DropdownMenuItem onClick={() => navigate("/admin/enquiries")} data-testid="menu-admin-enquiries">
                                <MessageSquare className="w-4 h-4 mr-2" /> Enquiries
                              </DropdownMenuItem>
                            )}
                            {permissions.faq_edit && (
                              <DropdownMenuItem onClick={() => navigate("/admin/faq")} data-testid="menu-admin-faq">
                                <HelpCircle className="w-4 h-4 mr-2" /> FAQ
                              </DropdownMenuItem>
                            )}
                            {permissions.tickers_edit && (
                              <DropdownMenuItem onClick={() => navigate("/admin/tickers")} data-testid="menu-admin-tickers">
                                <Megaphone className="w-4 h-4 mr-2" /> Announcements
                              </DropdownMenuItem>
                            )}
                            {permissions.pages_edit && (
                              <DropdownMenuItem onClick={() => navigate("/admin/pages")} data-testid="menu-admin-pages">
                                <LayoutTemplate className="w-4 h-4 mr-2" /> CMS Pages
                              </DropdownMenuItem>
                            )}
                            {permissions.approvals && (
                              <DropdownMenuItem onClick={() => navigate("/admin/approvals")} data-testid="menu-admin-approvals">
                                <ShieldCheck className="w-4 h-4 mr-2" /> Approvals
                              </DropdownMenuItem>
                            )}
                            {permissions.banners_edit && (
                              <DropdownMenuItem onClick={() => navigate("/admin/banners")} data-testid="menu-admin-banners">
                                <Megaphone className="w-4 h-4 mr-2" /> Promo Banners
                              </DropdownMenuItem>
                            )}
                            {permissions.team_management && (
                              <DropdownMenuItem onClick={() => navigate("/admin/team")} data-testid="menu-admin-team">
                                <Users className="w-4 h-4 mr-2" /> Team
                              </DropdownMenuItem>
                            )}
                            {permissions.settings && (
                              <DropdownMenuItem onClick={() => navigate("/admin/settings")} data-testid="menu-admin-settings">
                                <Settings className="w-4 h-4 mr-2" /> Site Settings
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuSubContent>
                        </DropdownMenuPortal>
                      </DropdownMenuSub>
                    </>
                  )}
                  {user.role === "vendor" && (
                    <DropdownMenuItem onClick={() => navigate("/vendor")} data-testid="menu-vendor">
                      <Briefcase className="w-4 h-4 mr-2" /> Vendor Dashboard
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout">
                    <LogOut className="w-4 h-4 mr-2" /> Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </div>
      {/* Row 2: Category Navigation Strip */}
      <div className="border-t border-border/50 bg-background/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="overflow-x-auto scrollbar-hide">
            <nav className="flex items-center gap-1 py-2 whitespace-nowrap" data-testid="category-nav-strip">
              {navCategories.map((cat) => {
                const Icon = getNavIcon(cat.icon);
                const catLink = cat.link_query ? `/search?${cat.link_query}` : `/search?category=${cat.product_category || cat.slug}`;
                const products = navProducts[cat.slug] || [];
                const hasProducts = products.length > 0;

                return (
                  <div key={cat.slug} className="shrink-0 relative" data-testid={`nav-${cat.slug}`}
                    onMouseEnter={hasProducts ? (e) => showDropdown(cat.slug, e) : undefined}
                    onMouseLeave={hasProducts ? hideDropdown : undefined}>
                    <Link to={catLink}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium hover:bg-teal/10 hover:text-teal transition-colors">
                      <Icon className="w-3.5 h-3.5 shrink-0" />
                      <span>{cat.name}</span>
                      {hasProducts && <ChevronDown className="w-3 h-3 opacity-50" />}
                    </Link>
                  </div>
                );
              })}
              <Link to="/blog" className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium hover:bg-teal/10 hover:text-teal transition-colors shrink-0" data-testid="nav-blog">Blog</Link>
            </nav>
          </div>
        </div>
      </div>

      {/* Fixed-position dropdown portal for nav products */}
      {openDropdown && navProducts[openDropdown] && (
        <div
          className="fixed z-[9999] animate-in fade-in-0 zoom-in-95 duration-150"
          style={{ top: dropdownPos.top, left: dropdownPos.left }}
          onMouseEnter={keepDropdown}
          onMouseLeave={hideDropdown}
          data-testid={`nav-${openDropdown}-dropdown`}
        >
          <div className="bg-popover border border-border rounded-lg shadow-lg py-1.5 min-w-[200px] max-w-[280px] mt-1">
            {navProducts[openDropdown].map((p) => {
              const catObj = navCategories.find((c) => c.slug === openDropdown);
              return (
                <Link key={p.id} to={`/${p.slug}`}
                  onClick={() => setOpenDropdown(null)}
                  className="block px-3 py-1.5 text-xs hover:bg-teal/10 hover:text-teal transition-colors truncate"
                  data-testid={`nav-product-${p.slug}`}>
                  {p.title}
                </Link>
              );
            })}
            <div className="border-t border-border/50 mt-1 pt-1">
              {(() => {
                const catObj = navCategories.find((c) => c.slug === openDropdown);
                const catLink = catObj?.link_query ? `/search?${catObj.link_query}` : `/search?category=${catObj?.product_category || openDropdown}`;
                return (
                  <Link to={catLink} onClick={() => setOpenDropdown(null)}
                    className="block px-3 py-1.5 text-xs font-semibold text-teal hover:bg-teal/10 transition-colors">
                    View all {catObj?.name || openDropdown} →
                  </Link>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
